import {
  faAngleDoubleRight,
  faBookOpen,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Lottie from 'lottie-light-react';
import React, { useEffect, useMemo, useState } from 'react';
import NewAchievementsModal from '../Achievements/NewAchievementsModal';
import generationInProgressAnimationData from '../animations/article_builder_inprogress.json';
import { trackInterest } from '../API/InterestApi';
import { useAuth } from '../Auth/AuthProvider';
import ChapterBuilderInlineView from '../Builder/ChapterBuilder/ChapterBuilderInlineView';
import DilemmaBuilderInlineView from '../Builder/DilemmaBuilder/DilemmaBuilderInlineView';
import Button from '../Components/Button';
import { useAchievements } from '../Context/AchievementsProvider';
import { AnalyticsEvent, useAnalytics } from '../Context/AnalyticsProvider';
import { useModals } from '../Context/ModalProvider';
import { useSession } from '../Context/ReadingSessionProvider';
import { useStreak } from '../Context/StreakProvider';
import Modal from '../Modal/Modal';
import { useNavigate } from '../ProtectedRoute';
import QuizView from '../Quiz/QuizView';
import { useBookChapterContext } from '../ReadBook/Context/BookChapterContextProvider';
import { Article } from '../Types/Article';
import { BookStatus } from '../Types/Book';
import { Question, QuestionType } from '../Types/Question';
import { QuestionResponse } from '../Types/QuestionResponse';
import { StorylineDilemma } from '../Types/StorylineDilemma';
import { UserAchievement } from '../Types/UserAchievement';
import { UserArticleQuizStage } from '../Types/UserArticle';
import { UserLootBox } from '../Types/UserLootBox';
import ContinueArticle from './ArticleViewComponents/ContinueArticle';
import { Section } from './Types/ArticleTypes';

export enum ReadArticleSidebarContentType {
  QUIZ = 'quiz',
  CHAPTER_BUILDER = 'chapter_builder',
  DILEMMA_BUILDER = 'dilemma_builder',
}

interface ReadArticleSidebarProps {
  article: Article | null;
  onNextQuestion: (question: Question, nextQuestion: Question | null) => void;
  onLoad: (stage: UserArticleQuizStage | null, sections: Section[]) => void;
  onContentTypeChange: (
    contentType: ReadArticleSidebarContentType | null,
  ) => void;
}

const ReadArticleSidebar: React.FC<ReadArticleSidebarProps> = ({
  article,
  onLoad,
  onNextQuestion,
  onContentTypeChange,
}) => {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const {
    registerUserAchievement,
    newAchievements: newUserAchievements,
    reloadAchievements,
  } = useAchievements();
  const { trackEvent } = useAnalytics();
  const { showModal } = useModals();
  const { updateStreak } = useStreak();
  const { flushActivityData } = useSession();
  const [isContinueArticleModalOpen, setIsContinueArticleModalOpen] =
    useState(false);
  const [quizStage, setQuizStage] = useState<UserArticleQuizStage | null>(null);
  const { book, chapter, ...chapterContext } = useBookChapterContext();

  useEffect(() => {
    console.debug(`${new Date().toISOString()}: Chapter changed`, chapter);
  }, [chapter]);

  const includeChapterBuilder = useMemo(() => {
    if (!book) return false;
    if (!chapter) return false;
    if (!chapter.canCreateNextChapter(userId)) return false;

    return !chapter.isLastChapter();
  }, [book, chapter, userId]);

  const includeDilemmaBuilder = useMemo(() => {
    if (!book) return false;
    if (!chapter) return false;
    if (!chapter.canCustomizeDilemmasForNextChapter(userId)) return false;

    return !chapter.isLastChapter();
  }, [book, chapter, userId]);

  const activeContentType = useMemo(() => {
    if (!quizStage) return null;

    if (quizStage === UserArticleQuizStage.GUIDING) {
      return ReadArticleSidebarContentType.QUIZ;
    }

    // If dilemma builder should be shown and hasn't been completed yet
    if (
      includeDilemmaBuilder &&
      !chapter.isDilemmasForNextChapterCustomized()
    ) {
      return ReadArticleSidebarContentType.DILEMMA_BUILDER;
    }

    // Original logic for chapter builder
    if (
      includeChapterBuilder &&
      !chapterContext.nextChapterGenerationStartedAt &&
      !chapterContext.nextChapterId
    )
      return ReadArticleSidebarContentType.CHAPTER_BUILDER;

    return ReadArticleSidebarContentType.QUIZ;
  }, [
    quizStage,
    book,
    chapter,
    article,
    includeDilemmaBuilder,
    includeChapterBuilder,
    chapterContext.nextChapterGenerationStartedAt,
    chapterContext.nextChapterId,
  ]);

  useEffect(() => {
    onContentTypeChange?.(activeContentType);
  }, [activeContentType]);

  const showNextSteps = quizStage === UserArticleQuizStage.REPORT;

  useEffect(() => {
    if (newUserAchievements?.length) {
      const handleClear = () => {
        reloadAchievements();
      };

      showModal({
        children: (
          <NewAchievementsModal
            newUserAchievements={newUserAchievements}
            onClear={handleClear}
          />
        ),
        onClose: handleClear,
        maxWidthClass:
          'max-w-[500px] max-md:max-w-[400px] max-sm:max-w-[350px]',
      });
    }
  }, [newUserAchievements, showModal]);

  useEffect(() => {
    if (!book) return;
    if (!chapter) return;

    // If the book has more chapters, set the next chapter id, so that the "Read Next Chapter" button is shown
    if (chapter.chapterNumber < book.numberOfChapters) {
      const nextChapter = book.chapters[chapter.chapterNumber];
      if (nextChapter) {
        chapterContext.registerNextChapterGenerationStarted(nextChapter.id, '');
      }
    }
  }, [book, chapter]);

  const handleContinueArticle = () => {
    setIsContinueArticleModalOpen(true);
  };

  const handleNextArticle = async () => {
    trackEvent(AnalyticsEvent.STORY_MOVED_TO_NEXT, {
      sourceArticleId: article?.id,
    });

    if (article && article.interests && article.interests.length > 0) {
      try {
        await trackInterest(userId, article.interests[0]);
      } catch (error) {
        throw new Error(`Failed to track interest: ${error}`);
      }
    } else {
      console.info('No interest tracked for article', {
        articleId: article?.id,
        title: article?.title,
        hasInterests: Boolean(article?.interests?.length),
      });
    }

    navigate(
      !article?.isLibraryArticle ? '/create-my-own-story' : '/choose-story',
    );
  };

  const handleQuizSectionComplete = (section: 'guiding' | 'assessment') => {
    if (section === 'guiding') {
      setQuizStage(UserArticleQuizStage.ASSESSMENT);
    }
  };

  const handleChapterGenerationStarting = async () => {
    chapterContext.registerNextChapterGeneraitonStarting();
    // Flush activity data before moving back to quiz
    await flushActivityData();
  };

  const handleChapterGenerationStarted = (
    chapterId: string,
    requestId: string,
  ) => {
    chapterContext.registerNextChapterGenerationStarted(chapterId, requestId);
  };

  const handleChapterGenerationError = () => {
    // Reset the generation state
    chapterContext.resetNextChapterGeneration();
  };

  const handleGoToNextChapter = (
    chapterId: string,
    requestId?: string,
    generationStartedAt?: Date,
  ) => {
    navigate(`/create-my-own-story/${book!.id}/${chapterId}`, {
      requestId,
      generationStartedAt,
    });
  };

  const handlePublishBook = () => {
    navigate(`/book/${book!.id}#publish`);
  };

  const handleQuizLoaded = async (
    currentQuestion: Question,
    questions: Question[],
    questionResponses: QuestionResponse[],
  ) => {
    const sections = splitPassageInSections(
      article.passage,
      questions.filter(
        (question) => question.questionType === QuestionType.GUIDING,
      ),
      questionResponses,
    );

    const quizStage =
      questions.length > 0
        ? !currentQuestion
          ? UserArticleQuizStage.REPORT
          : currentQuestion.questionType === QuestionType.GUIDING
            ? UserArticleQuizStage.GUIDING
            : UserArticleQuizStage.ASSESSMENT
        : null;

    setQuizStage(quizStage);
    onLoad?.(quizStage, sections);
  };

  const handleQuestionAnswered = async (
    question: Question,
    questionResponse: QuestionResponse,
    questions: Question[],
    questionResponses: QuestionResponse[],
    userAchievements: UserAchievement[],
    lootBox: UserLootBox | null,
  ) => {
    if (userAchievements) {
      if (userAchievements.length) {
        for (const userAchievement of userAchievements) {
          registerUserAchievement(userAchievement);
        }
      } else {
        reloadAchievements();
      }
    }

    if (shouldStreakIncrement()) {
      await updateStreak();
    }

    function shouldStreakIncrement(): boolean {
      // Check if we're on the last question
      const incompleteQuestions = questionResponses.filter(
        (q) => !q.isCompleted,
      );
      if (incompleteQuestions.length !== 1) return false;

      const questionsById = Object.fromEntries(
        questions.map((question) => [question.id, question]),
      );

      // Check if we have at most 1 mistake
      const numberOfAssessmentMistakes = questionResponses.filter(
        (q) =>
          questionsById[q.questionId].questionType ===
            QuestionType.ASSESSMENT && q.answerState === 'incorrect',
      ).length;

      return numberOfAssessmentMistakes <= 1;
    }
  };

  const handleNextQuestion = async (
    currentQuestion: Question,
    nextQuestion: Question,
  ) => {
    onNextQuestion?.(currentQuestion, nextQuestion);
  };

  const handleQuizCompleted = async (
    questions: Question[],
    questionResponses: QuestionResponse[],
  ) => {
    setQuizStage(UserArticleQuizStage.REPORT);
  };

  const splitPassageInSections = (
    passage: string,
    guidingQuestions: Question[],
    questionResponses: QuestionResponse[],
  ) => {
    console.log('Splitting passage in sections');
    const orderedQuestions = guidingQuestions
      .slice()
      .sort((questionA, questionB) => {
        return questionA.order - questionB.order;
      });
    const sections: Section[] = [];
    let currentStartIndex = 0;
    for (let i = 0; i < orderedQuestions.length; i++) {
      const question = orderedQuestions[i];

      currentStartIndex = Math.min(
        currentStartIndex,
        question.relatedPassageStartIndex,
      );
      sections.push({
        text: passage.slice(currentStartIndex, question.relatedPassageEndIndex),
        questionId: question.id,
        startIndex: currentStartIndex,
        endIndex: question.relatedPassageEndIndex,
        isBlurred:
          i > 0 && // First section is always visible
          // Next seections are blurred if the previous question was not answered
          !questionResponses.find(
            (q) => q.questionId === orderedQuestions[i - 1].id,
          )?.isCompleted,
      });
      currentStartIndex = question.relatedPassageEndIndex + 1;
    }

    if (currentStartIndex < passage.length) {
      sections.push({
        text: passage.slice(currentStartIndex),
        questionId: null,
        startIndex: currentStartIndex,
        endIndex: passage.length - 1,
        // Last section is blurred if the last guided question was not answered
        isBlurred: !questionResponses.find(
          (q) =>
            q.questionId === orderedQuestions[orderedQuestions.length - 1].id,
        )?.isCompleted,
      });
    }

    return sections;
  };

  const handleDilemmaSubmitting = (
    dilemma: StorylineDilemma,
    selectedOptionIds: string[],
  ) => {
    chapterContext.registerNextChapterDilemmaCustomized(
      dilemma,
      selectedOptionIds,
    );
  };

  const handleDilemmaError = (
    dilemma: StorylineDilemma,
    selectedOptionIds: string[],
  ) => {
    chapterContext.resetNextChapterDilemmaCustomized(
      dilemma,
      selectedOptionIds,
    );
  };

  return (
    <div
      className="
      flex flex-col overflow-auto items-stretch gap-4
      sm:w-[32%] sm:max-w-[480px] sm:sticky sm:top-3 sm:h-[calc(100dvh-24px)]
      max-lg:gap-2
    "
    >
      {showNextSteps ? (
        !book ? (
          <NextStepsSingularArticle />
        ) : chapter.chapterNumber < book.numberOfChapters ? (
          <NextStepsGoToBookChapter
            chapterId={chapterContext.nextChapterId}
            requestId={chapterContext.nextChapterGenerationRequestId}
            generationStartedAt={chapterContext.nextChapterGenerationStartedAt}
          />
        ) : book.isAmongBookAuthors(userId) ? (
          <NextStepsPublishBook />
        ) : (
          <></>
        )
      ) : null}
      <div
        className={`
        flex-1 overflow-auto  
        text-left
        max-sm:w-full
        max-md:max-w-full
        sm:rounded-xl sm:max-md:mt-6 sm:min-h-60 sm:border sm:border-[color:var(--base-border,#E4E7EB)] sm:bg-gray-50 sm:border-solid
      `}
      >
        {includeDilemmaBuilder ? (
          <div
            className={
              activeContentType !==
              ReadArticleSidebarContentType.DILEMMA_BUILDER
                ? 'hidden'
                : ''
            }
          >
            <DilemmaBuilderInlineView
              book={book}
              chapter={chapter}
              dilemma={chapter?.dilemmas?.[0]}
              onSubmitting={handleDilemmaSubmitting}
              onError={handleDilemmaError}
            />
          </div>
        ) : null}

        {includeChapterBuilder ? (
          <div
            className={
              activeContentType !==
              ReadArticleSidebarContentType.CHAPTER_BUILDER
                ? 'hidden'
                : ''
            }
          >
            <ChapterBuilderInlineView
              key={article?.id}
              book={book}
              lastChapter={chapter}
              allowCreate={!book.isCollaborative()}
              preSubmitPrompt={{
                title: `One Last Step Before Chapter ${chapter.chapterNumber + 1}!`,
                description: `Before you can read what happens next, show your understanding of Chapter ${chapter.chapterNumber} with a quick quiz.`,
              }}
              submitButtonText={`Take Chapter ${chapter.chapterNumber} Quiz`}
              onChapterGenerationStarting={handleChapterGenerationStarting}
              onChapterGenerationStarted={handleChapterGenerationStarted}
              onChapterGenerationError={handleChapterGenerationError}
            />
          </div>
        ) : null}

        <div
          className={
            activeContentType !== ReadArticleSidebarContentType.QUIZ
              ? 'hidden'
              : ''
          }
        >
          <QuizView
            key={article?.id}
            article={article}
            onLoad={handleQuizLoaded}
            onQuestionAnswered={handleQuestionAnswered}
            onNextQuestion={handleNextQuestion}
            onQuizComplete={handleQuizCompleted}
            onQuizSectionComplete={handleQuizSectionComplete}
          />
        </div>

        <div className={activeContentType !== null ? 'hidden' : ''}>
          <Lottie
            animationData={generationInProgressAnimationData}
            loop={true}
          />
          <div
            className="
                  relative mb-auto text-center text-base font-medium text-gray-800
                "
          >
            Your quiz is coming right up!
          </div>
          <div className="relative mt-1 text-center opacity-50 max-md:mr-2.5 line-clamp-2">
            Show your knowledge and unlock the rest of the chapter
          </div>
        </div>
      </div>
    </div>
  );

  function NextStepsSingularArticle() {
    return (
      <div
        className={`
    overflow-auto  
    text-left
    max-sm:w-full
    max-md:max-w-full
    sm:rounded-xl sm:max-md:mt-6 sm:border sm:border-blue-200 sm:bg-blue-100 sm:border-solid
  `}
      >
        <div className="items-start flex w-full flex-col pt-3 px-4 max-md:max-w-full max-md:px-5 sticky bottom-0">
          <div className="text-black text-xl py-2 font-semibold">Next Step</div>
          <div className="border-b border-blue-200 w-[calc(100%+2rem)] mx-[-1rem]"></div>
          <div className="mt-1 pt-3 pb-3 flex flex-col lg:flex-row gap-2 max-sm:w-full">
            {!article?.isLibraryArticle ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleContinueArticle}
                >
                  <FontAwesomeIcon icon={faAngleDoubleRight} /> Continue This
                  Story
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleNextArticle}
                >
                  <FontAwesomeIcon icon={faPen} /> Create Another Story
                </Button>
              </>
            ) : (
              <Button
                testId="read-another-story-button"
                type="button"
                variant="primary"
                onClick={handleNextArticle}
              >
                <FontAwesomeIcon icon={faBookOpen} /> Read Another Story
              </Button>
            )}

            <Modal
              isOpen={isContinueArticleModalOpen}
              onClose={() => setIsContinueArticleModalOpen(false)}
              showCloseButton={true}
            >
              <ContinueArticle article={article} />
            </Modal>
          </div>
        </div>
      </div>
    );
  }

  function NextStepsGoToBookChapter({
    chapterId,
    requestId,
    generationStartedAt,
  }: {
    chapterId: string;
    requestId?: string;
    generationStartedAt: Date | null;
  }) {
    const { userId } = useAuth();
    const canAccessNextChapter =
      chapter.canCreateNextChapter(userId) ||
      book.getChapter(chapter.chapterNumber + 1)?.canReadChapter(userId);

    return (
      <div
        className={`
    overflow-auto  
    text-left
    max-sm:w-full
    max-md:max-w-full
    sm:rounded-xl sm:max-md:mt-6 sm:border sm:border-blue-200 sm:bg-blue-100 sm:border-solid
  `}
      >
        <div className="items-start flex w-full flex-col pt-3 px-4 max-md:max-w-full max-md:px-5 sticky bottom-0">
          <div className="text-black text-xl py-2 font-semibold">
            {canAccessNextChapter
              ? `Ready for Chapter ${chapter.chapterNumber + 1}?`
              : 'Chapter Complete!'}
          </div>
          <p className="text-gray-600">
            {canAccessNextChapter
              ? 'Your next chapter awaits!'
              : book.isAbandoned()
                ? 'Your friend stopped writing this book with you.'
                : "Now it's your friend's turn to work on the next chapter."}
          </p>
          <div className="border-b border-blue-200 w-[calc(100%+2rem)] mx-[-1rem]"></div>
          <div className="mt-1 pt-3 pb-3 flex flex-col lg:flex-row gap-2 max-sm:w-full">
            {canAccessNextChapter ? (
              <Button
                type="button"
                variant="primary"
                onClick={() =>
                  handleGoToNextChapter(
                    chapterId,
                    requestId,
                    generationStartedAt,
                  )
                }
                data-chapter-id={chapterId}
                data-request-id={requestId}
                data-generation-started-at={generationStartedAt}
              >
                Start Reading Chapter {chapter.chapterNumber + 1}
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/book/${book.id}`)}
              >
                {book.isAbandoned()
                  ? 'Back to book'
                  : `Wait for Chapter ${chapter.chapterNumber + 1}`}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  function NextStepsPublishBook() {
    return (
      <div
        className={`
    overflow-auto  
    text-left
    max-sm:w-full
    max-md:max-w-full
    sm:rounded-xl sm:max-md:mt-6 sm:border sm:border-blue-200 sm:bg-blue-100 sm:border-solid
  `}
      >
        <div className="items-start flex w-full flex-col pt-3 px-4 max-md:max-w-full max-md:px-5 sticky bottom-0">
          <div className="text-black text-xl py-2 font-semibold">
            Book Complete!
          </div>
          <div className="border-b border-blue-200 w-[calc(100%+2rem)] mx-[-1rem]"></div>
          {book.status === BookStatus.DRAFT ? (
            <>
              <div className="flex flex-col gap-2 text-gray-500 mt-3">
                <p>
                  You've written all {book.numberOfChapters} chapters of your
                  magical tale. Ready to share it with the world?
                </p>
                <p>
                  You'll be able to add a title, generate a cover, and share
                  your story.
                </p>
              </div>
              <div className="mt-1 pt-3 pb-3 flex flex-col lg:flex-row gap-2 max-sm:w-full">
                <Button
                  testId="read-another-story-button"
                  type="button"
                  variant="primary"
                  onClick={handlePublishBook}
                >
                  Publish Your Book
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2 text-gray-500 mt-3">
                <p>
                  You've written all {book.numberOfChapters} chapters of your
                  magical tale.
                </p>
              </div>
              <div className="mt-1 pt-3 pb-3 flex flex-col lg:flex-row gap-2 max-sm:w-full">
                <Button
                  testId="read-another-story-button"
                  mode="link"
                  href={`/book/${book.id}`}
                  variant="primary"
                >
                  See Published Book
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
};

export default ReadArticleSidebar;
